import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Spin, Table, Input, Button, DatePicker, Drawer, Space, Select, Radio } from 'antd';
import {
  DollarOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  RiseOutlined,
  SearchOutlined,
  ReloadOutlined,
  FilterOutlined,
  WalletOutlined,
  GiftOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import StatCard from '../../../../components/StatCard';
import ReportService from '../../../../services/ReportService';

const { RangePicker } = DatePicker;

dayjs.extend(isBetween);

const SalesSummaries = () => {
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [salesData, setSalesData] = useState([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [totalCari, setTotalCari] = useState(0);
  const [totalIkram, setTotalIkram] = useState(0);
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [dateFilterType, setDateFilterType] = useState('quick');
  const [dateFilter, setDateFilter] = useState('today');
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [dateRange, setDateRange] = useState([dayjs().subtract(1, 'day'), dayjs()]);
  const [branchCode, setBranchCode] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [draftDateFilterType, setDraftDateFilterType] = useState('quick');
  const [draftDateFilter, setDraftDateFilter] = useState('today');
  const [draftSelectedDate, setDraftSelectedDate] = useState(dayjs());
  const [draftDateRange, setDraftDateRange] = useState([dayjs().subtract(1, 'day'), dayjs()]);
  const [draftBranchCode, setDraftBranchCode] = useState('');

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const token = localStorage.getItem("token") || localStorage.getItem("x-access-token");
  const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
  const companyCode = userDetails?.CompanyCode || null;

  const fetchBranches = async () => {
    if (!companyCode) {
      setBranches([]);
      setBranchesLoading(false);
      return;
    }
    try {
      setBranchesLoading(true);
      const response = await ReportService.company.getBranches({
        token,
        CompanyCode: companyCode,
      });
      if (response.status === 200) {
        const branchList = Array.isArray(response.data) ? response.data : response.data?.data || [];
        setBranches(branchList);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      setBranches([]);
    } finally {
      setBranchesLoading(false);
    }
  };

  const fetchSalesDataWithParams = async (customDateFilter = null, customDateFilterType = null, customDateRange = null, customSelectedDate = null, customBranchCode = null) => {
    try {
      setSalesLoading(true);
      const currentDateFilterType = customDateFilterType !== null ? customDateFilterType : dateFilterType;
      const currentDateFilter = customDateFilter !== null ? customDateFilter : dateFilter;
      const currentDateRange = customDateRange !== null ? customDateRange : dateRange;
      const currentSelectedDate = customSelectedDate !== null ? customSelectedDate : selectedDate;
      const currentBranchCode = customBranchCode !== null ? customBranchCode : branchCode;

      const params = {
        token,
        CompanyCode: companyCode,
      };

      if (currentBranchCode && currentBranchCode.trim() !== '') {
        params.BranchCode = currentBranchCode;
      }

      if (currentDateFilterType === 'range' && currentDateRange && currentDateRange[0] && currentDateRange[1]) {
        params.StartDate = currentDateRange[0].format('YYYY-MM-DD');
        params.EndDate = currentDateRange[1].format('YYYY-MM-DD');
      } else if (currentDateFilterType === 'quick' && currentDateFilter === 'custom') {
        params.StartDate = currentSelectedDate.format('YYYY-MM-DD');
        params.EndDate = currentSelectedDate.format('YYYY-MM-DD');
      } else {
        params.DateFilter = currentDateFilter;
      }

      const response = await ReportService.sales.getSalesSummaries(params);
      if (response.status === 200) {
        const rows = response.data.data || [];

        const flatList = [];

        rows.forEach((r, parentIndex) => {
          flatList.push({
            key: String(parentIndex),
            product: r.UrunAdi,
            portion: r.PorsiyonAdi,
            quantity: Number(r.Miktar) || 0,
            totalPrice: Number(r.ToplamSatis) || 0,
            parentTotalPrice: Number(r.ParentToplamSatis) || 0,
            isParent: r.IsParent || false,
            isChild: false,
          });

          if (r.children && Array.isArray(r.children) && r.children.length > 0) {
            const childMap = new Map();
            r.children.forEach((child) => {
              const key = `${child.UrunAdi}_${child.PorsiyonAdi}`;
              if (childMap.has(key)) {
                const existing = childMap.get(key);
                existing.quantity += Number(child.Miktar) || 0;
                existing.totalPrice += Number(child.ToplamSatis) || 0;
              } else {
                childMap.set(key, {
                  product: child.UrunAdi,
                  portion: child.PorsiyonAdi,
                  quantity: Number(child.Miktar) || 0,
                  totalPrice: Number(child.ToplamSatis) || 0,
                });
              }
            });

            let childIndex = 0;
            childMap.forEach((groupedChild) => {
              flatList.push({
                key: `${parentIndex}-${childIndex++}`,
                product: groupedChild.product,
                portion: groupedChild.portion,
                quantity: groupedChild.quantity,
                totalPrice: groupedChild.totalPrice,
                parentTotalPrice: 0,
                isParent: false,
                isChild: true,
              });
            });
          }
        });

        setSalesData(flatList);
        setTotalCari(Number(response.data?.totalCari || 0));
        setTotalIkram(Number(response.data?.totalIkram || 0));
      }
    } catch (error) {
      console.error('Error fetching sales data:', error);
    } finally {
      setSalesLoading(false);
    }
  };

  const fetchSalesData = async () => {
    return fetchSalesDataWithParams();
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchSalesData(),
      fetchBranches()
    ]).finally(() => {
      setLoading(false);
    });
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value);
  };

  const salesColumns = [
    {
      title: 'Ürün',
      dataIndex: 'product',
      key: 'product',
      width: '40%',
      render: (text, record) => (
        <span
          className={`text-sm ${record.isChild ? 'text-gray-600 pl-6' : 'font-medium'}`}
          style={record.isChild ? { display: 'block' } : {}}
        >
          {record.isChild && '└─ '}
          {text}
        </span>
      )
    },
    {
      title: 'Porsiyon',
      dataIndex: 'portion',
      key: 'portion',
      width: isMobile ? '25%' : '25%',
      render: (text, record) => (
        <span className={`text-sm ${record.isChild ? 'text-gray-600' : ''}`}>{text}</span>
      )
    },
    {
      title: 'Adet',
      dataIndex: 'quantity',
      key: 'quantity',
      width: isMobile ? '15%' : '15%',
      render: (value, record) => (
        <span className={`text-sm ${record.isChild ? 'text-gray-600' : 'font-medium'}`}>{value}</span>
      )
    },
    {
      title: 'Toplam',
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      width: isMobile ? '20%' : '20%',
      render: (value, record) => (
        <span className={`font-semibold ${record.isChild ? 'text-gray-600' : 'text-green-600'}`}>{formatCurrency(value)}</span>
      )
    },
  ];

  const filteredSalesData = salesData.filter((row) => {
    if (searchText) {
      const needle = searchText.toLowerCase();
      const matches = (
        String(row.product || '').toLowerCase().includes(needle) ||
        String(row.portion || '').toLowerCase().includes(needle)
      );
      if (!matches) return false;
    }
    return true;
  });

  const derivedStats = useMemo(() => {

    const parentProducts = filteredSalesData.filter((r) => r.isParent === true);
    const totalRows = filteredSalesData.length;
    const totalSales = filteredSalesData.reduce((sum, r) => sum + Number(r.parentTotalPrice || 0), 0);
    const totalQuantity = parentProducts.reduce((sum, r) => sum + Number(r.quantity || 0), 0);
    const averageProductPrice = totalQuantity > 0 ? totalSales / totalQuantity : 0;

    const productQty = new Map();
    parentProducts.forEach((r) => {
      const q = Number(r.quantity || 0);
      if (r.product) productQty.set(r.product, (productQty.get(r.product) || 0) + q);
    });
    const topProduct = Array.from(productQty.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] || '-';

    return { totalRows, totalSales, totalQuantity, averageProductPrice, topProduct };
  }, [filteredSalesData]);

  const openFilters = () => {
    setDraftDateFilterType(dateFilterType);
    setDraftDateFilter(dateFilter);
    setDraftSelectedDate(selectedDate);
    setDraftDateRange(dateRange);
    setDraftBranchCode(branchCode);
    setIsFilterOpen(true);
  };

  const applyFilters = () => {
    setDateFilterType(draftDateFilterType);
    setDateFilter(draftDateFilter);
    setSelectedDate(draftSelectedDate);
    setDateRange(draftDateRange);
    setBranchCode(draftBranchCode);
    setIsFilterOpen(false);

    if (draftDateFilterType === 'range') {
      fetchSalesDataWithParams(null, 'range', draftDateRange, null, draftBranchCode);
    } else if (draftDateFilter === 'custom') {
      fetchSalesDataWithParams('custom', 'quick', null, draftSelectedDate, draftBranchCode);
    } else {
      fetchSalesDataWithParams(draftDateFilter, 'quick', null, null, draftBranchCode);
    }
  };

  const clearFilters = () => {
    const defaultDateFilterType = 'quick';
    const defaultDateFilter = 'today';
    const defaultSelectedDate = dayjs();
    const defaultDateRange = [dayjs().subtract(1, 'day'), dayjs()];
    const defaultBranchCode = '';

    setDraftDateFilterType(defaultDateFilterType);
    setDraftDateFilter(defaultDateFilter);
    setDraftSelectedDate(defaultSelectedDate);
    setDraftDateRange(defaultDateRange);
    setDraftBranchCode(defaultBranchCode);

    setDateFilterType(defaultDateFilterType);
    setDateFilter(defaultDateFilter);
    setSelectedDate(defaultSelectedDate);
    setDateRange(defaultDateRange);
    setBranchCode(defaultBranchCode);
    fetchSalesDataWithParams(defaultDateFilter, defaultDateFilterType, null, null, null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between px-1 sm:px-0 py-1 sm:py-2">
        <div className="flex items-center gap-2">
          <ShoppingCartOutlined className="text-blue-600" />
          <h1 className="text-base sm:text-lg lg:text-xl font-semibold text-slate-800">Satış Özetleri</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button icon={<FilterOutlined />} onClick={openFilters}>Filtreler</Button>
        </div>
      </div>

      <Row gutter={[12, 12]} className="mt-1">
        <Col xs={12} sm={12} lg={6}>
          <StatCard
            title="Toplam Satış"
            value={formatCurrency(derivedStats.totalSales)}
            suffix=""
            icon={<DollarOutlined />}
            gradientFrom="from-emerald-100"
            gradientTo="to-white"
            isMobile={isMobile}
            compact
          />
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <StatCard
            title="Toplam Adet"
            value={derivedStats.totalQuantity}
            suffix="adet"
            icon={<ShoppingCartOutlined />}
            gradientFrom="from-blue-100"
            gradientTo="to-white"
            isMobile={isMobile}
            compact
          />
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <StatCard
            title="Toplam Cari"
            value={formatCurrency(totalCari)}
            suffix=""
            icon={<WalletOutlined />}
            gradientFrom="from-red-100"
            gradientTo="to-white"
            isMobile={isMobile}
            compact
          />
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <StatCard
            title="Toplam İkram"
            value={formatCurrency(totalIkram)}
            suffix=""
            icon={<GiftOutlined />}
            gradientFrom="from-pink-100"
            gradientTo="to-white"
            isMobile={isMobile}
            compact
          />
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <StatCard
            title="Ortalama Ürün Fiyatı"
            value={formatCurrency(derivedStats.averageProductPrice)}
            suffix=""
            icon={<RiseOutlined />}
            gradientFrom="from-purple-100"
            gradientTo="to-white"
            isMobile={isMobile}
            compact
          />
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <StatCard
            title="En Çok Satan"
            value={derivedStats.topProduct}
            suffix=""
            icon={<UserOutlined />}
            gradientFrom="from-orange-100"
            gradientTo="to-white"
            isMobile={isMobile}
            compact
          />
        </Col>
      </Row>

      <div className="mt-4 sm:mt-5">
        <Card title={<div className="mt-2"><h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold`}>Satış Detayları</h2></div>} bordered={false} className="shadow-lg rounded-2xl border border-gray-100 bg-white" bodyStyle={{ padding: isMobile ? 12 : 18 }}>
          <div className="mb-3 flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-2">
            <div className="flex items-center gap-2 w-full lg:w-auto">
              <Input allowClear value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder="Müşteri, ürün veya sipariş no ara..." prefix={<SearchOutlined className="text-gray-400" />} className="w-full sm:w-80" />
              <Button onClick={() => fetchSalesData()} loading={salesLoading} icon={<ReloadOutlined />}>Yenile</Button>
            </div>
          </div>

          <Table
            columns={salesColumns}
            dataSource={filteredSalesData}
            loading={salesLoading}
            rowKey="key"
            sticky
            pagination={{ defaultPageSize: isMobile ? 5 : 10, size: isMobile ? 'small' : 'default', showTotal: (total) => (<span className={`${isMobile ? 'text-sm' : 'text-base'}`}>Toplam {total} kayıt</span>), showSizeChanger: !isMobile }}
            className="sales-table [&_.ant-table-thead_tr_th]:bg-gray-50 [&_.ant-table-row:hover]:bg-blue-50/30"
            size={isMobile ? 'small' : 'middle'}
            rowClassName={(record) => {
              const baseClass = isMobile ? 'text-[13px]' : 'text-[15px]';
              if (record.isChild) {
                return `${baseClass} bg-gray-50/50`;
              }
              return baseClass;
            }}
            scroll={isMobile ? { x: true } : undefined}
          />
        </Card>
      </div>

      <Drawer title="Filtreler" placement="right" open={isFilterOpen} onClose={() => setIsFilterOpen(false)} width={isMobile ? 280 : 360}>
        <Space direction="vertical" size="middle" className="w-full">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">Filtre Tipi</span>
            <Radio.Group
              value={draftDateFilterType}
              onChange={(e) => setDraftDateFilterType(e.target.value)}
              className="w-full"
            >
              <Radio.Button value="quick" className="w-1/2 text-center">Gün</Radio.Button>
              <Radio.Button value="range" className="w-1/2 text-center">Tarih Aralığı</Radio.Button>
            </Radio.Group>
          </div>

          {draftDateFilterType === 'quick' ? (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-slate-500">Gün Seçimi</span>
                <div className="flex flex-col gap-2">
                  {[
                    { label: 'Bugün', value: 'today' },
                    { label: 'Dün', value: 'yesterday' },
                    { label: 'Bu Hafta', value: 'week' },
                    { label: 'Bu Ay', value: 'month' },
                    { label: 'Gün Seçin', value: 'custom' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setDraftDateFilter(option.value)}
                      className={`
                        px-3 py-2 text-sm font-semibold transition-all duration-300 ease-out rounded-lg
                        ${draftDateFilter === option.value
                          ? 'bg-gradient-to-r from-rose-500 to-red-600 text-white shadow-lg'
                          : 'text-slate-600 hover:text-slate-800 hover:bg-gray-100 border border-gray-200'
                        }
                      `}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              {draftDateFilter === 'custom' && (
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-slate-500">Özel Gün</span>
                  <DatePicker
                    value={draftSelectedDate}
                    onChange={(date) => setDraftSelectedDate(date)}
                    format="DD.MM.YYYY"
                    className="w-full"
                    placeholder="Gün seçin"
                  />
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col gap-1">
              <span className="text-xs text-slate-500">Tarih Aralığı</span>
              <RangePicker
                value={draftDateRange}
                onChange={(dates) => setDraftDateRange(dates || [])}
                format="DD.MM.YYYY"
                className="w-full"
              />
            </div>
          )}

          <div className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">Şube</span>
            <Select
              value={draftBranchCode}
              onChange={(value) => setDraftBranchCode(value)}
              placeholder="Şube seçiniz"
              className="w-full"
              loading={branchesLoading}
              allowClear
            >
              <Select.Option value="">Tüm Şubeler</Select.Option>
              {branches.map((branch) => (
                <Select.Option key={branch.ID} value={branch.SubeKodu}>
                  {branch.SubeAdi}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div className="flex items-center justify-between gap-2 pt-2">
            <Button onClick={clearFilters}>Temizle</Button>
            <Button type="primary" danger ghost={false} onClick={applyFilters} className="!bg-rose-600 !border-rose-600 !text-white hover:!bg-rose-700 hover:!border-rose-700">Uygula</Button>
          </div>
        </Space>
      </Drawer>
    </div>
  );
};

export default SalesSummaries;